import type { Validations, Violation } from "@credit-acceptance/step6-bff-client";

// ---------------------------------------------------------------------------
// serverViolations
//
// Shared shape for translating a BFF `Validations` payload (requiredViolations /
// businessRuleViolations) onto a form's RHF field paths — the Signers.violations
// pattern, generic over the form. A violation whose `field` doesn't resolve to a
// rendered input, or that has no `field` at all, lands in `unmapped` so the
// section can still surface it in its banner instead of dropping it.
// ---------------------------------------------------------------------------

export type ViolationSource = "required" | "businessRule";

export interface FieldErrorTarget<TPath extends string> {
  rhfPath: TPath;
  message: string;
  source: ViolationSource;
  code: string;
}

export interface UnmappedViolation {
  violation: Violation;
  source: ViolationSource;
}

export interface TranslatedViolations<TPath extends string> {
  fieldErrors: Array<FieldErrorTarget<TPath>>;
  unmapped: UnmappedViolation[];
}

/**
 * Flattens both buckets, required first, tagging each violation with the bucket it came from.
 * @param {Validations | undefined} validations - The BFF validations.
 * @returns {Array<[Violation, ViolationSource]>} The tagged violations.
 */
export function tagViolations(validations: Validations | undefined): Array<[Violation, ViolationSource]> {
  return [
    ...(validations?.requiredViolations ?? []).map((violation): [Violation, ViolationSource] => [
      violation,
      "required",
    ]),
    ...(validations?.businessRuleViolations ?? []).map((violation): [Violation, ViolationSource] => [
      violation,
      "businessRule",
    ]),
  ];
}

/**
 * Translates validations with a section-specific resolver.
 * @template TPath
 * @param {Validations | undefined} validations - The BFF validations.
 * @param {(violation: Violation) => TPath | undefined} resolve - Returns the RHF path for a violation, or
 *   undefined when it names no rendered input.
 * @returns {TranslatedViolations<TPath>} Field errors plus anything left unmapped.
 */
export function translateViolations<TPath extends string>(
  validations: Validations | undefined,
  resolve: (violation: Violation) => TPath | undefined
): TranslatedViolations<TPath> {
  const fieldErrors: Array<FieldErrorTarget<TPath>> = [];
  const unmapped: UnmappedViolation[] = [];
  for (const [violation, source] of tagViolations(validations)) {
    const rhfPath = resolve(violation);
    if (rhfPath) {
      fieldErrors.push({ rhfPath, message: violation.message, source, code: violation.code });
    } else {
      unmapped.push({ violation, source });
    }
  }
  return { fieldErrors, unmapped };
}

/**
 * Drops the optional section prefix (`vehicle.`, `vsc.`, ...) from a violation's dot-path field.
 * @param {string} field - The violation's `field`.
 * @param {string[]} prefixes - Accepted leading segments, matched case-insensitively.
 * @returns {string} The field relative to the section.
 */
export function stripFieldPrefix(field: string, prefixes: readonly string[]): string {
  const [head, ...rest] = field.split(".");
  return rest.length > 0 && prefixes.some((prefix) => prefix.toLowerCase() === head.toLowerCase())
    ? rest.join(".")
    : field;
}

/**
 * Unmapped messages for a section banner, de-duplicated in order.
 * @param {UnmappedViolation[]} unmapped - The unmapped violations.
 * @param {(violation: Violation) => string} [format] - Optional message formatter.
 * @returns {string[]} The messages.
 */
export function unmappedMessages(
  unmapped: UnmappedViolation[],
  format: (violation: Violation) => string = (violation) => violation.message
): string[] {
  return [...new Set(unmapped.map(({ violation }) => format(violation)))];
}
